import React, { ReactElement, useEffect, useMemo, useState } from "react";
import {
  makeStyles,
  Paper,
  TextField,
  Typography,
  InputAdornment,
} from "@material-ui/core";
import { useEnumStrings } from "../enumStrings";
import Loader from "./Loader/Loader";

type NonDuesRevenueCalculatorProps = {
  totalMembers: number;
  chamberOrBoardText: string;
  isLoading?: boolean;
};

const goldColor = "#E1A731";

// Tier configuration for sponsorship calculation
// (Duplicated from backend for dynamic calculator updates)
const SPONSORSHIP_TIERS = {
  TIER_1: { maxMembers: 300, amount: 1200 },
  TIER_2: { maxMembers: 600, amount: 2400 },
  TIER_3: { maxMembers: 900, amount: 3600 },
  TIER_4: { maxMembers: Infinity, amount: 4800 },
};

/**
 * Default percentage of members expected to advertise as featured members.
 * Used for calculating potential revenue estimates.
 * (Duplicated from backend for dynamic calculator updates)
 */
export const FEATURED_MEMBER_DEFAULT_ADVERTISING_PERCENTAGE = 20; // 20%

const getSponsorshipByMemberCount = (memberCount: number): number => {
  if (memberCount < SPONSORSHIP_TIERS.TIER_1.maxMembers)
    return SPONSORSHIP_TIERS.TIER_1.amount;
  if (memberCount < SPONSORSHIP_TIERS.TIER_2.maxMembers)
    return SPONSORSHIP_TIERS.TIER_2.amount;
  if (memberCount < SPONSORSHIP_TIERS.TIER_3.maxMembers)
    return SPONSORSHIP_TIERS.TIER_3.amount;
  return SPONSORSHIP_TIERS.TIER_4.amount;
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    width: "100%",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  mainTitle: {
    fontSize: "22px",
    fontWeight: 700,
    marginBottom: -5,
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  subtitle: {
    fontSize: "19px",
    color: "#666",
  },
  calculatorCard: {
    border: "5px solid #F5C84C",
    borderRadius: 24,
    padding: theme.spacing(3),
    backgroundColor: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    boxShadow: "0px 0px 10px 1px lightgrey",
    minHeight: 160,
    height: "100%",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  descriptionText: {
    fontSize: "18px",
    color: "#000",
    lineHeight: "2.5",
  },
  outputBox: {
    backgroundColor: "#FFFFFF",
    border: `1px solid ${goldColor}`,
    borderRadius: "30px",
    padding: theme.spacing(2, 3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: "fit-content",
  },
  outputAmount: {
    fontSize: "28px",
    fontWeight: 700,
    color: goldColor,
  },
  outputLabel: {
    fontSize: "19px",
    fontWeight: 700,
    color: "#000000",
  },
  amountPill: {
    backgroundColor: "#FFFFFF",
    border: `1px solid ${goldColor}`,
    borderRadius: "30px",
    padding: theme.spacing(1, 3),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "fit-content",
    height: "50px",
  },
  divider: {
    height: "1px",
    backgroundColor: "#E0E0E0",
    margin: theme.spacing(1, 0),
  },
  questionText: {
    fontSize: "16px",
    fontWeight: 500,
    color: "#333",
    marginBottom: theme.spacing(1),
  },
  noteText: {
    fontSize: "15px",
    color: "#000",
    lineHeight: "2.5",
  },

  totalRow: {
    display: "flex",
    alignItems: "center",
    // justifyContent: "space-between",
    gap: theme.spacing(2),
    // flexWrap: "wrap",
  },

  inputField: {
    minWidth: "20px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontWeight: 700,
      fontSize: "24px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: goldColor,
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: goldColor,
      },
      "&.Mui-focused fieldset": {
        borderColor: goldColor,
      },
    },
    "& input": {
      textAlign: "center",
      padding: "10px",
      fontWeight: 700,
    },
  },
  loaderWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    boxShadow: "0px 0px 10px 1px lightgrey",
    border: "5px solid #F5C84C",
  },
}));

const NonDuesRevenueCalculator = ({
  totalMembers,
  chamberOrBoardText,
  isLoading = false,
}: NonDuesRevenueCalculatorProps): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const strings = enumStrings.revenueCalculator;

  // State for calculator inputs
  const [numberOfMembers, setNumberOfMembers] = useState<number>(
    totalMembers || 400
  );
  const [advertisingPercentage, setAdvertisingPercentage] = useState<number>(
    FEATURED_MEMBER_DEFAULT_ADVERTISING_PERCENTAGE
  );
  const [numberOfCategoryBanners, setNumberOfCategoryBanners] =
    useState<number>(16);

  // State for Title Sponsorship (now editable)
  const [titleSponsorship, setTitleSponsorship] = useState<number>(0);

  // Update numberOfMembers when totalMembers prop changes (e.g., chamber selection changes)
  useEffect(() => {
    if (totalMembers && totalMembers > 0) {
      setNumberOfMembers(totalMembers);
    }
  }, [totalMembers]);

  // Update titleSponsorship when numberOfMembers changes (dynamic calculation)
  useEffect(() => {
    setTitleSponsorship(getSponsorshipByMemberCount(numberOfMembers));
  }, [numberOfMembers]);

  // Calculate Feature Member Banner Revenue
  const featureMemberRevenue = useMemo(() => {
    return ((numberOfMembers * advertisingPercentage) / 100) * 250;
  }, [numberOfMembers, advertisingPercentage]);

  // Calculate Category Banner Sales Revenue
  const categoryBannerRevenue = useMemo(() => {
    return numberOfCategoryBanners * 250;
  }, [numberOfCategoryBanners]);

  // Calculate Total Revenue
  const totalRevenue = useMemo(() => {
    return featureMemberRevenue + titleSponsorship + categoryBannerRevenue;
  }, [featureMemberRevenue, titleSponsorship, categoryBannerRevenue]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `$${Math.round(amount).toLocaleString()}`;
  };

  const handleNumberOfMembersChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value) || 0;
    setNumberOfMembers(Math.max(0, value));
  };

  const handleAdvertisingPercentageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value) || 0;
    setAdvertisingPercentage(Math.max(0, value));
  };

  const handleCategoryBannersChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value) || 0;
    setNumberOfCategoryBanners(Math.max(0, Math.min(16, value)));
  };

  const handleTitleSponsorshipChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value) || 0;
    setTitleSponsorship(Math.max(0, value));
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.loaderWrapper}>
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.mainTitle}>
          {strings.mainTitle.replace("{{chamberOrBoard}}", chamberOrBoardText)}
        </div>
        <Typography className={classes.subtitle}>{strings.subtitle}</Typography>
      </div>

      {/* Calculator Card */}
      <Paper className={classes.calculatorCard} elevation={0}>
        {/* Section 1: Feature Member Banner */}
        <div className={classes.section}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <Typography className={classes.descriptionText}>
              {strings.enterNumberOfMembers}{" "}
              <TextField
                type="number"
                value={numberOfMembers}
                onChange={handleNumberOfMembersChange}
                variant="outlined"
                className={classes.inputField}
                inputProps={{ min: 0 }}
                style={{ width: "100px", verticalAlign: "bottom" }}
              />{" "}
              {strings.enterThe}{" "}
              <TextField
                type="number"
                value={advertisingPercentage}
                onChange={handleAdvertisingPercentageChange}
                variant="outlined"
                className={classes.inputField}
                inputProps={{ min: 0 }}
                style={{ width: "80px", verticalAlign: "bottom" }}
              />{" "}
              {strings.ofMemberBusinesses} {strings.featureMemberDescription}
            </Typography>
          </div>
          <Typography className={classes.descriptionText}></Typography>

          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className={classes.amountPill}>
              <span className={classes.outputAmount}>
                {formatCurrency(featureMemberRevenue)}
              </span>
            </div>
            <Typography className={classes.outputLabel}>
              {strings.featureMemberBannerRevenue}
            </Typography>
          </div>
        </div>

        {/* Section 2: Title Sponsorship */}
        <div className={classes.section}>
          <Typography className={classes.descriptionText}>
            {strings.titleSponsorshipQuestion.replace(
              "{{chamberOrBoard}}",
              chamberOrBoardText.toLowerCase()
            )}
          </Typography>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <TextField
              type="number"
              value={titleSponsorship}
              onChange={handleTitleSponsorshipChange}
              variant="outlined"
              className={classes.inputField}
              style={{ width: "150px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        color: "#333",
                      }}
                    >
                      $
                    </span>
                  </InputAdornment>
                ),
              }}
            />
            <Typography className={classes.noteText}>
              {strings.titleSponsorshipNote.replace(
                "{{chamberOrBoard}}",
                chamberOrBoardText
              )}
            </Typography>
          </div>
        </div>

        {/* Section 3: Category Banners */}
        <div className={classes.section}>
          <Typography className={classes.descriptionText}>
            {strings.categoryBannerDescription.replace(
              "{{chamberOrBoard}}",
              chamberOrBoardText.toLowerCase()
            )}
          </Typography>
          <div className={classes.inputRow} style={{ alignItems: "center" }}>
            <TextField
              type="number"
              value={numberOfCategoryBanners}
              onChange={handleCategoryBannersChange}
              variant="outlined"
              className={classes.inputField}
              inputProps={{ min: 0, max: 16 }}
              style={{ width: "100px" }}
            />
            <Typography
              style={{ fontSize: "24px", fontWeight: 700, margin: "0 8px" }}
            >
              =
            </Typography>
            <div className={classes.amountPill}>
              <span className={classes.outputAmount}>
                {formatCurrency(categoryBannerRevenue)}
              </span>
            </div>
            <span className={classes.outputLabel}>
              {strings.categoryBannerSalesRevenue}
            </span>
          </div>
        </div>

        {/* Total Section */}
        <div className={classes.totalRow}>
          <span className={classes.outputLabel} style={{ fontSize: "21px" }}>
            {strings.totalLabel.replace(
              "{{chamberOrBoard}}",
              chamberOrBoardText
            )}
          </span>
          <div className={classes.amountPill}>
            <span className={classes.outputAmount}>
              {formatCurrency(totalRevenue)}
            </span>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default NonDuesRevenueCalculator;
